import { User } from '../types';

const API_URL = '/api';
const TOKEN_KEY = 'authToken';
const SIM_CODE_KEY = 'selectedSimCode';
let currentSimCode: string | null = sessionStorage.getItem(SIM_CODE_KEY);

// --- API Client ---
const apiClient = {
    async get(endpoint: string, options?: { headers?: Record<string, string> }) {
        return this.request('GET', endpoint, undefined, options);
    },

    async post(endpoint: string, body: any, options?: { headers?: Record<string, string> }) {
        return this.request('POST', endpoint, body, options);
    },

    async put(endpoint: string, body?: any, options?: { headers?: Record<string, string> }) {
        return this.request('PUT', endpoint, body, options);
    },
    
    async delete(endpoint: string, options?: { headers?: Record<string, string> }) {
        return this.request('DELETE', endpoint, undefined, options);
    },

    async request(method: string, endpoint: string, body?: any, options?: { headers?: Record<string, string> }) {
        const headers: HeadersInit = {
            'Content-Type': 'application/json',
            ...(options?.headers || {})
        };
        const token = getToken();
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
        // Se um x-simulado foi passado nos options, ele tem prioridade, caso contrário usa o global
        if (!(headers as any)['x-simulado'] && currentSimCode) {
            (headers as any)['x-simulado'] = currentSimCode;
        }

        const response = await fetch(`${API_URL}${endpoint}`, {
            method,
            headers,
            body: body ? JSON.stringify(body) : undefined,
        });

        if (!response.ok) {
            let errorData;
            try {
                errorData = await response.json();
                console.error(`API Error (${response.status}):`, errorData);
            } catch {
                // Se não conseguir fazer parse do JSON, usar a mensagem do status
                const statusText = response.statusText || 'Unknown error';
                const statusCode = response.status;
                errorData = { 
                    message: statusCode === 401 ? 'Unauthorized' : 
                             statusCode === 403 ? 'Forbidden: Admin access required' :
                             statusCode === 404 ? 'Not found' :
                             statusCode === 500 ? 'Internal server error' :
                             `Error ${statusCode}: ${statusText}`
                };
                console.error(`API Error (${statusCode}):`, errorData);
            }
            throw new Error(errorData.message || 'Request failed');
        }

        if (response.status === 204) { // No Content
            return null;
        }

        return response.json();
    },
};


// --- Token Management ---
export const getToken = (): string | null => {
    return sessionStorage.getItem(TOKEN_KEY);
};

const setToken = (token: string): void => {
    sessionStorage.setItem(TOKEN_KEY, token);
};

const removeToken = (): void => {
    sessionStorage.removeItem(TOKEN_KEY);
};


export const setApiClientSimCode = (code: string | null): void => {
    currentSimCode = code;
    if (code) {
        sessionStorage.setItem(SIM_CODE_KEY, code);
    } else {
        sessionStorage.removeItem(SIM_CODE_KEY);
    }
};

// --- Auth Service Functions ---

export const registerUser = async (firstName: string, lastName: string, email: string, password: string): Promise<{ email: string }> => {
    if (!firstName || !lastName || !email || !password) {
        throw new Error("Todos os campos são obrigatórios.");
    }
    const data = await apiClient.post('/register', { firstName, lastName, email, password });
    return data;
};

export const verifyEmail = async (email: string, code: string): Promise<void> => {
    await apiClient.post('/verify-email', { email, code });
};

export const resendVerificationCode = async (email: string): Promise<void> => {
    await apiClient.post('/resend-code', { email });
};

export const loginUser = async (email: string, password: string): Promise<User> => {
    const data = await apiClient.post('/login', { email, password });
    if (data.token && data.user) {
        setToken(data.token);
        return data.user;
    }
    throw new Error('Login failed: No token received.');
};

export const logoutUser = async (): Promise<void> => {
    try {
        await apiClient.post('/logout', {});
    } catch (e) {
    } finally {
        removeToken();
    }
};

export const getCurrentUser = async (): Promise<User | null> => {
    const token = getToken();
    if (!token) return null;
    try {
        const data = await apiClient.get('/user');
        return data.user;
    } catch (error) {
        console.error("Session check failed", error);
        removeToken(); // Clear invalid token
        return null;
    }
};

export default apiClient;
export const setSimCode = (code: string | null) => {
    currentSimCode = code || null;
};
